/**
 * 🔄 SELF-HEALING CIRCUIT BREAKER SYSTEM
 * Automatic retry, fallback, and recovery mechanisms for frontend
 */

class CircuitBreaker {
  constructor(options = {}) {
    this.failureThreshold = options.failureThreshold || 5;
    this.recoveryTimeout = options.recoveryTimeout || 30000; // 30 seconds
    this.monitoringPeriod = options.monitoringPeriod || 10000; // 10 seconds
    
    // Circuit states
    this.states = {
      CLOSED: 'CLOSED',     // Normal operation
      OPEN: 'OPEN',         // Circuit is open, requests fail fast
      HALF_OPEN: 'HALF_OPEN' // Testing if service has recovered
    };
    
    this.state = this.states.CLOSED;
    this.failures = 0;
    this.lastFailureTime = null;
    this.successCount = 0;
    
    // Callbacks for different states
    this.onStateChange = options.onStateChange || (() => {});
    this.onFallback = options.onFallback || (() => {});
  }

  async execute(request, fallback = null) {
    if (this.state === this.states.OPEN) {
      return this.handleOpenCircuit(fallback);
    }

    if (this.state === this.states.HALF_OPEN) {
      return this.handleHalfOpenCircuit(request, fallback);
    }

    // CLOSED state - normal execution
    return this.handleClosedCircuit(request, fallback);
  }

  async handleOpenCircuit(fallback) {
    // Check if we should attempt recovery
    if (Date.now() - this.lastFailureTime > this.recoveryTimeout) {
      console.log('🔄 Circuit Breaker: Attempting recovery...');
      this.setState(this.states.HALF_OPEN);
      return this.execute(null, fallback); // Retry with half-open
    }

    // Circuit is open, use fallback
    console.log('⚡ Circuit Breaker: OPEN - Using fallback');
    return this.useFallback(fallback, 'Circuit is open');
  }

  async handleHalfOpenCircuit(request, fallback) {
    try {
      console.log('🔍 Circuit Breaker: HALF-OPEN - Testing service...');
      const result = await request();
      
      // Success! Close the circuit
      this.successCount++;
      if (this.successCount >= 2) { // Require 2 successes to fully recover
        console.log('✅ Circuit Breaker: Service recovered - CLOSING circuit');
        this.reset();
      }
      
      return result;
    } catch (error) {
      console.log('❌ Circuit Breaker: Recovery failed - OPENING circuit');
      this.recordFailure();
      return this.useFallback(fallback, error.message);
    }
  }

  async handleClosedCircuit(request, fallback) {
    try {
      const result = await request();
      this.reset();
      return result;
    } catch (error) {
      console.log(`❌ Circuit Breaker: Request failed - ${error.message}`);
      this.recordFailure();
      
      if (this.failures >= this.failureThreshold) {
        console.log('🚨 Circuit Breaker: Failure threshold reached - OPENING circuit');
        this.setState(this.states.OPEN);
      }
      
      return this.useFallback(fallback, error.message);
    }
  }

  recordFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();
    this.successCount = 0;
  }

  reset() {
    this.failures = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    if (this.state !== this.states.CLOSED) {
      this.setState(this.states.CLOSED);
    }
  }

  setState(newState) {
    const oldState = this.state;
    this.state = newState;
    console.log(`🔄 Circuit Breaker: State changed from ${oldState} to ${newState}`);
    this.onStateChange(oldState, newState);
  }

  async useFallback(fallback, error) {
    this.onFallback(error);
    
    if (typeof fallback === 'function') {
      try {
        return await fallback();
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError.message);
        throw new Error(`Primary service failed: ${error}. Fallback failed: ${fallbackError.message}`);
      }
    }
    
    if (fallback !== null) {
      return fallback;
    }
    
    throw new Error(`Service unavailable: ${error}`);
  }

  getStatus() {
    return {
      state: this.state,
      failures: this.failures,
      lastFailureTime: this.lastFailureTime,
      successCount: this.successCount
    };
  }
}

// 🔄 SMART RETRY SYSTEM with exponential backoff
class SmartRetry {
  constructor(options = {}) {
    this.maxAttempts = options.maxAttempts || 3;
    this.baseDelay = options.baseDelay || 1000;
    this.maxDelay = options.maxDelay || 10000;
    this.backoffMultiplier = options.backoffMultiplier || 2;
    this.jitter = options.jitter !== false; // Add randomness by default
  }

  async execute(request, shouldRetry = null) {
    let lastError;
    
    for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
      try {
        console.log(`🔄 Smart Retry: Attempt ${attempt}/${this.maxAttempts}`);
        const result = await request();
        
        if (attempt > 1) {
          console.log(`✅ Smart Retry: Success on attempt ${attempt}`);
        }
        
        return result;
      } catch (error) {
        lastError = error;
        
        // Check if we should retry this error
        if (shouldRetry && !shouldRetry(error, attempt)) {
          console.log(`🛑 Smart Retry: Not retrying error: ${error.message}`);
          throw error;
        }
        
        // Don't delay after the last attempt
        if (attempt < this.maxAttempts) {
          const delay = this.calculateDelay(attempt);
          console.log(`⏳ Smart Retry: Waiting ${delay}ms before attempt ${attempt + 1}`);
          await this.sleep(delay);
        }
      }
    }
    
    console.log(`❌ Smart Retry: All ${this.maxAttempts} attempts failed`);
    throw lastError;
  }

  calculateDelay(attempt) {
    let delay = this.baseDelay * Math.pow(this.backoffMultiplier, attempt - 1);
    delay = Math.min(delay, this.maxDelay);
    
    if (this.jitter) {
      // Add ±25% random jitter
      const jitterAmount = delay * 0.25;
      delay += (Math.random() - 0.5) * 2 * jitterAmount;
    }
    
    return Math.round(delay);
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// 🛡️ SELF-HEALING API CLIENT
class SelfHealingApiClient {
  constructor(options = {}) {
    this.circuitBreaker = new CircuitBreaker({
      failureThreshold: options.failureThreshold || 3,
      recoveryTimeout: options.recoveryTimeout || 15000,
      onStateChange: this.handleStateChange.bind(this),
      onFallback: this.handleFallback.bind(this)
    });
    
    this.retry = new SmartRetry({
      maxAttempts: options.maxRetries || 3,
      baseDelay: options.retryDelay || 1000
    });

    this.fallbackData = new Map();
    this.healthStatus = 'unknown';
  }

  async makeRequest(config) {
    return this.circuitBreaker.execute(
      () => this.retry.execute(() => this.executeRequest(config)),
      () => this.getFallbackResponse(config)
    );
  }

  async executeRequest(config) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout || 8000);

    try {
      const response = await fetch(config.url, {
        ...config,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache successful responses for fallback
      this.fallbackData.set(config.url, {
        data,
        timestamp: Date.now(),
        ttl: config.fallbackTtl || 300000 // 5 minutes
      });

      this.healthStatus = 'healthy';
      return data;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      
      this.healthStatus = 'unhealthy';
      throw error;
    }
  }

  getFallbackResponse(config) {
    const cached = this.fallbackData.get(config.url);
    
    if (cached && (Date.now() - cached.timestamp) < cached.ttl) {
      console.log('📦 Using cached fallback data');
      return {
        ...cached.data,
        _fallback: true,
        _cached: true
      };
    }

    // Return mock/default data based on endpoint
    if (config.url.includes('/login')) {
      return {
        success: false,
        error: '⚠️ Service temporarily unavailable. Please try again in a few minutes.',
        _fallback: true
      };
    }

    if (config.url.includes('/api-key')) {
      return {
        apiKey: null,
        error: '⚠️ Unable to fetch API key. Service is recovering.',
        _fallback: true
      };
    }

    return {
      error: '⚠️ Service temporarily unavailable',
      _fallback: true
    };
  }

  handleStateChange(oldState, newState) {
    console.log(`🔄 Circuit breaker: ${oldState} → ${newState}`);
    
    // Emit events for UI updates
    window.dispatchEvent(new CustomEvent('circuit-breaker-state-change', {
      detail: { oldState, newState, status: this.getStatus() }
    }));
  }

  handleFallback(error) {
    console.log('🛡️ Using fallback due to:', error);
    
    window.dispatchEvent(new CustomEvent('service-fallback', {
      detail: { error, timestamp: Date.now() }
    }));
  }

  getStatus() {
    return {
      circuitBreaker: this.circuitBreaker.getStatus(),
      healthStatus: this.healthStatus,
      fallbackCacheSize: this.fallbackData.size
    };
  }
}

// Create singleton instance
export const selfHealingClient = new SelfHealingApiClient({
  failureThreshold: 3,
  recoveryTimeout: 15000,
  maxRetries: 3,
  retryDelay: 1000
});

export { CircuitBreaker, SmartRetry, SelfHealingApiClient };